import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  IconRobot,
  IconSend,
  IconMicrophone,
  IconMicrophoneOff,
  IconPlayerStop,
  IconTrash,
  IconCopy,
  IconRefresh,
  IconFileTypePdf,
  IconFile,
  IconDownload,
} from "@tabler/icons-react";
import {
  Form,
  Button,
  Badge,
  Toast,
  ToastContainer,
  Alert,
} from "react-bootstrap";
import usePublicAgent from "../../../hooks/usePublicAgent";
import { Hash } from "../../../helpers/Helpers";
import { API_URL } from "../../../api/Urls";

const PublicChat = ({ agent, sessionToken, agentToken, onBack }) => {
  const { sendMessage, resetChat, error: serviceError } = usePublicAgent();

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Estados de áudio
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioMimeType, setAudioMimeType] = useState("audio/wav");

  // Estados de UI
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("info");

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const audioChunks = useRef([]);

  const showNotification = (message, type = "info") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, scrollToBottom]);

  // Processamento de mensagens com múltiplas partes
  const processImageBreaks = (messageText) => {
    if (!messageText || typeof messageText !== "string")
      return [{ type: "text", content: messageText }];

    const imageUrlRegex =
      /(https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp|bmp|svg))/gi;
    const imageUrls = messageText.match(imageUrlRegex);

    if (!imageUrls || imageUrls.length === 0) {
      return [{ type: "text", content: messageText }];
    }

    const parts = [];
    let remainingText = messageText;

    imageUrls.forEach((imageUrl) => {
      const splitResult = remainingText.split(imageUrl);

      if (splitResult[0].trim()) {
        parts.push({
          type: "text",
          content: splitResult[0].trim(),
        });
      }

      parts.push({
        type: "image",
        content: imageUrl,
      });

      remainingText = splitResult.slice(1).join(imageUrl);
    });

    if (remainingText.trim()) {
      parts.push({
        type: "text",
        content: remainingText.trim(),
      });
    }

    return parts;
  };

  // Extrai um nome de arquivo amigável a partir da URL (S3 não usa querystring)
  const getFileNameFromUrl = (url) => {
    try {
      const path = decodeURIComponent(url.split("?")[0].split("#")[0]);
      const name = path.substring(path.lastIndexOf("/") + 1);
      return name || "documento";
    } catch (e) {
      return "documento";
    }
  };

  // Detecta URLs de documentos (PDF e afins) em texto e as transforma em
  // partes do tipo "document", evitando exibir a URL crua no chat.
  const processDocumentBreaks = (messageText) => {
    if (!messageText || typeof messageText !== "string")
      return [{ type: "text", content: messageText }];

    const docUrlRegex =
      /(https?:\/\/\S+\.(?:pdf|docx?|xlsx?|pptx?|csv|txt|zip|rar))/gi;
    const docUrls = messageText.match(docUrlRegex);

    if (!docUrls || docUrls.length === 0) {
      return [{ type: "text", content: messageText }];
    }

    const parts = [];
    let remainingText = messageText;

    docUrls.forEach((docUrl) => {
      const splitResult = remainingText.split(docUrl);

      if (splitResult[0].trim()) {
        parts.push({
          type: "text",
          content: splitResult[0].trim(),
        });
      }

      parts.push({
        type: "document",
        content: docUrl,
        fileName: getFileNameFromUrl(docUrl),
      });

      remainingText = splitResult.slice(1).join(docUrl);
    });

    if (remainingText.trim()) {
      parts.push({
        type: "text",
        content: remainingText.trim(),
      });
    }

    return parts;
  };

  // Dispara o download via proxy do backend (Content-Disposition: attachment),
  // forçando o download direto sem abrir o PDF e sem expor a URL do S3.
  const handleFileDownload = (url, fileName) => {
    const name = fileName || getFileNameFromUrl(url);
    const proxyUrl =
      `${API_URL}agents/public/download/` +
      `?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(name)}`;
    const link = document.createElement("a");
    link.href = proxyUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const processMessageBreaks = (messageText) => {
    if (!messageText || typeof messageText !== "string") return [messageText];

    if (!messageText.includes("[[break]]")) {
      return [messageText];
    }

    return messageText
      .split("[[break]]")
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
  };

  const processAllBreaks = (messageText) => {
    if (!messageText || typeof messageText !== "string")
      return [{ type: "text", content: messageText }];

    const breakParts = processMessageBreaks(messageText);
    const allParts = [];

    breakParts.forEach((part) => {
      const imageParts = processImageBreaks(part);
      // Para cada trecho de texto restante, ainda detectar links de documentos
      imageParts.forEach((imagePart) => {
        if (imagePart.type === "text") {
          allParts.push(...processDocumentBreaks(imagePart.content));
        } else {
          allParts.push(imagePart);
        }
      });
    });

    return allParts;
  };

  const renderMessageContent = (text, messageType = "text", fileName) => {
    if (typeof text !== "string") return null;

    const cleanText = text.replace(/\[\[break\]\]/g, "").trim();

    if (
      messageType === "document" ||
      messageType === "pdf" ||
      messageType === "file"
    ) {
      const url = cleanText;
      const name = fileName || getFileNameFromUrl(url);
      const isPdf =
        /\.pdf(\?|#|$)/i.test(url) || name.toLowerCase().endsWith(".pdf");

      return (
        <button
          type="button"
          onClick={() => handleFileDownload(url, name)}
          className="d-flex align-items-center gap-2 p-2 my-2 text-start w-100"
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            cursor: "pointer",
            maxWidth: "280px",
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: isPdf ? "#fee2e2" : "#e0e7ff",
              color: isPdf ? "#dc2626" : "#4f46e5",
            }}
          >
            {isPdf ? <IconFileTypePdf size={22} /> : <IconFile size={22} />}
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div
              className="fw-semibold text-truncate"
              style={{ fontSize: "14px", color: "#1e293b" }}
            >
              {name}
            </div>
            <small className="text-muted">Clique para baixar</small>
          </div>
          <IconDownload size={18} className="text-muted flex-shrink-0" />
        </button>
      );
    }

    if (messageType === "image") {
      return (
        <div className="d-block w-100 my-3">
          <img
            src={cleanText}
            alt="Imagem"
            className="img-fluid rounded"
            style={{
              maxWidth: "250px",
              maxHeight: "200px",
              objectFit: "cover",
            }}
            onError={(e) => {
              e.target.outerHTML = `
                <div class="text-muted p-3 bg-light border rounded" style="max-width: 250px;">
                  <small>📷 Imagem não pôde ser carregada</small><br/>
                  <a href="${cleanText}" target="_blank" rel="noopener noreferrer" class="text-decoration-none">
                    Ver imagem original
                  </a>
                </div>
              `;
            }}
          />
        </div>
      );
    }

    return (
      <span>
        {cleanText.split("\n").map((line, index) => (
          <span key={index}>
            {line}
            {index < cleanText.split("\n").length - 1 && <br />}
          </span>
        ))}
      </span>
    );
  };

  const getTypingDelay = (content, contentType = "text") => {
    const baseDelay = 800;

    if (contentType === "image") {
      return 1200;
    }

    const charDelay = content.length * 25;
    const maxDelay = 2500;

    return Math.min(baseDelay + charDelay, maxDelay);
  };

  const simulateTyping = (duration) => {
    return new Promise((resolve) => setTimeout(resolve, duration));
  };

  const processMultiPartMessage = async (messageParts) => {
    for (let i = 0; i < messageParts.length; i++) {
      const part = messageParts[i];
      const content = part.content || part;
      const contentType = part.type || "text";

      if (i > 0) {
        const delay = getTypingDelay(content, contentType);
        await simulateTyping(delay);
      }

      const aiMessage = {
        id: Date.now() + i + 1 + Math.random() * 1000,
        text: content,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isPartOf: messageParts.length > 1,
        partIndex: i + 1,
        totalParts: messageParts.length,
        messageType: contentType,
        fileName: part.fileName,
      };

      setMessages((prev) => [...prev, aiMessage]);
      setTimeout(() => scrollToBottom(), 100);
    }
  };

  // Funcionalidades de áudio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });

      // Usar formato de áudio compatível com OpenAI Whisper (MP3, WAV, M4A, OGG)
      let mimeType = "audio/wav";
      if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      } else if (MediaRecorder.isTypeSupported("audio/wav")) {
        mimeType = "audio/wav";
      } else if (MediaRecorder.isTypeSupported("audio/ogg")) {
        mimeType = "audio/ogg";
      }

      setAudioMimeType(mimeType);

      const recorder = new MediaRecorder(stream, {
        mimeType: mimeType,
      });

      audioChunks.current = [];

      recorder.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, {
          type: mimeType,
        });
        setAudioBlob(audioBlob);

        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio(audioUrl);

        stream.getTracks().forEach((track) => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      showNotification("Gravação iniciada. Fale agora!", "info");
    } catch (error) {
      console.error("Erro ao iniciar gravação:", error);
      showNotification("Erro ao acessar microfone", "error");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
      setIsRecording(false);
      showNotification("Gravação finalizada!", "success");
    }
  };

  const clearRecordedAudio = () => {
    if (recordedAudio) {
      URL.revokeObjectURL(recordedAudio);
    }
    setRecordedAudio(null);
    setAudioBlob(null);
  };

  const sendAudioMessage = async () => {
    if (!audioBlob || !agent) return;

    try {
      setIsLoading(true);

      const audioMessage = {
        id: Date.now(),
        text: "🎤 Mensagem de áudio",
        audioUrl: recordedAudio,
        isUser: true,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isAudio: true,
      };

      setMessages((prev) => [...prev, audioMessage]);

      const formData = new FormData();

      // Determinar extensão do arquivo baseado no mimeType
      let fileExtension = "wav";
      if (audioBlob.type.includes("mp4")) fileExtension = "m4a";
      else if (audioBlob.type.includes("ogg")) fileExtension = "ogg";
      else if (audioBlob.type.includes("mp3")) fileExtension = "mp3";

      formData.append("audio", audioBlob, `audio.${fileExtension}`);

      const response = await sendMessage(agentToken, formData);

      if (response) {
        // AgentFlow: usar messages[] humanizadas com delay
        if (response.isAgentFlow && response.messages && response.messages.length > 0) {
          for (let i = 0; i < response.messages.length; i++) {
            const msg = response.messages[i];
            const delay = i > 0 ? (msg.delay_before_ms || 800) : 0;

            if (delay > 0) {
              await simulateTyping(delay);
            }

            if (msg.is_media && msg.text) {
              try {
                const mediaData = JSON.parse(msg.text);
                if (mediaData.type === "media") {
                  await processMultiPartMessage([{
                    type: mediaData.media_type || "image",
                    content: mediaData.url,
                    fileName: mediaData.filename,
                  }]);
                  continue;
                }
              } catch (e) {}
            }

            const msgParts = processAllBreaks(msg.text);
            if (msgParts.length === 1 && msgParts[0].type === "text") {
              setMessages((prev) => [...prev, {
                id: Date.now() + i + 1 + Math.random() * 1000,
                text: msgParts[0].content,
                isUser: false,
                timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                messageType: "text",
              }]);
            } else {
              await processMultiPartMessage(msgParts);
            }
          }
        } else {
          // Legado: usar response.data como string
          let responseText = "";
          if (response.status === 200 && response.data) {
            responseText = response.data;
          } else if (response.message) {
            responseText = response.message;
          } else {
            responseText = "Resposta de áudio recebida";
          }

          const messageParts = processAllBreaks(responseText);

          if (messageParts.length === 1 && messageParts[0].type === "text") {
            const aiResponse = {
              id: Date.now() + 1,
              text: messageParts[0].content,
              isUser: false,
              timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              messageType: "text",
            };
            setMessages((prev) => [...prev, aiResponse]);
          } else {
            await processMultiPartMessage(messageParts);
          }
        }
      }

      clearRecordedAudio();
    } catch (error) {
      console.error("Erro ao enviar áudio:", error);

      let errorText = "Erro ao enviar mensagem de áudio";

      if (error.response) {
        const { status, data } = error.response;
        const apiError = data?.error || data?.message || "Erro desconhecido";

        switch (status) {
          case 400:
            errorText = `❌ Erro de validação: ${apiError}`;
            break;
          case 401:
            errorText = "🔐 Sessão expirada. Recarregue a página.";
            break;
          case 429:
            errorText = "⏱️ Muitas mensagens. Aguarde um momento.";
            break;
          case 500:
            errorText = "🔧 Erro interno. Tente novamente.";
            break;
          default:
            errorText = `❌ Erro ${status}: ${apiError}`;
        }
      }

      const errorMessage = {
        id: Date.now() + 1,
        text: errorText,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !agent) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      isUser: true,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const messagePayload = {
        message: inputMessage,
        sessionToken: sessionToken,
      };

      const response = await sendMessage(agentToken, messagePayload);

      if (response) {
        // AgentFlow: usar messages[] humanizadas com delay
        if (response.isAgentFlow && response.messages && response.messages.length > 0) {
          for (let i = 0; i < response.messages.length; i++) {
            const msg = response.messages[i];
            const delay = i > 0 ? (msg.delay_before_ms || 800) : 0;

            if (delay > 0) {
              await simulateTyping(delay);
            }

            if (msg.is_media && msg.text) {
              try {
                const mediaData = JSON.parse(msg.text);
                if (mediaData.type === "media") {
                  await processMultiPartMessage([{
                    type: mediaData.media_type || "image",
                    content: mediaData.url,
                    fileName: mediaData.filename,
                  }]);
                  continue;
                }
              } catch (e) {}
            }

            const msgParts = processAllBreaks(msg.text);
            if (msgParts.length === 1 && msgParts[0].type === "text") {
              setMessages((prev) => [...prev, {
                id: Date.now() + i + 1 + Math.random() * 1000,
                text: msgParts[0].content,
                isUser: false,
                timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                messageType: "text",
                isPartOf: response.messages.length > 1,
                partIndex: i + 1,
                totalParts: response.messages.length,
              }]);
            } else {
              await processMultiPartMessage(msgParts);
            }
          }
        } else {
          // Legado: usar response.data como string
          let responseText = "";
          if (response.status === 200 && response.data) {
            responseText = response.data;
          } else if (response.message) {
            responseText = response.message;
          } else {
            responseText = "Resposta recebida";
          }

          const messageParts = processAllBreaks(responseText);

          if (messageParts.length === 1 && messageParts[0].type === "text") {
            const aiMessage = {
              id: Date.now() + 1,
              text: messageParts[0].content,
              isUser: false,
              timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              messageType: "text",
            };
            setMessages((prev) => [...prev, aiMessage]);
          } else {
            await processMultiPartMessage(messageParts);
          }
        }

        setTimeout(() => {
          inputRef.current?.focus();
        }, 200);
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);

      let errorText = "Falha na comunicação com o agente";

      if (error.response) {
        const { status, data } = error.response;
        const apiError = data?.error || data?.message || "Erro desconhecido";

        switch (status) {
          case 400:
            errorText = `❌ Erro de validação: ${apiError}`;
            break;
          case 401:
            errorText = "🔐 Sessão expirada. Recarregue a página.";
            break;
          case 429:
            errorText =
              "⏱️ Muitas mensagens. Aguarde um momento antes de enviar outra.";
            break;
          case 500:
            errorText =
              "🔧 Erro interno do servidor. Tente novamente em alguns minutos.";
            break;
          default:
            errorText = `❌ Erro ${status}: ${apiError}`;
        }
      } else if (error.message) {
        errorText = `❌ ${error.message}`;
      }

      const errorMessage = {
        id: Date.now() + 1,
        text: errorText,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearConversation = async () => {
    try {
      setMessages([]);

      const messagePayload = {
        message: "resetar",
        sessionToken: sessionToken,
      };

      await sendMessage(agentToken, messagePayload);

      showNotification("Conversa resetada com sucesso!", "success");
    } catch (error) {
      console.warn("Erro ao resetar conversa:", error);
      showNotification("Erro ao resetar conversa", "error");
    }

    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  };

  const copyConversation = () => {
    const conversationText = messages
      .filter((msg) => !msg.isWelcome)
      .map(
        (msg) =>
          `${msg.isUser ? "Você" : agent?.agent_name || "Agente"}: ${msg.text}`
      )
      .join("\n\n");

    navigator.clipboard.writeText(conversationText);
    showNotification("Conversa copiada para área de transferência!", "success");
  };

  const renderMessage = (message) => {
    const getBubbleClasses = () => {
      let classes = "d-flex mb-3 ";
      classes += message.isUser
        ? "justify-content-end"
        : "justify-content-start";
      return classes;
    };

    const getContentClasses = () => {
      let classes = "position-relative ";
      if (message.isError) {
        classes += "bg-danger text-white fw-bold";
      } else if (message.isWarning) {
        classes += "bg-warning text-dark";
      } else if (message.isUser) {
        classes += "text-white";
      } else {
        classes += "bg-white border";
      }
      return classes;
    };

    const getMessageStyle = () => {
      const baseStyle = {
        maxWidth: "85%",
        wordWrap: "break-word",
        fontSize: "15px",
        lineHeight: "1.5",
        padding: "12px 16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      };

      if (message.isUser) {
        baseStyle.borderRadius = "20px 20px 6px 20px";
        baseStyle.background = "var(--gradient-primary)";
        baseStyle.marginLeft = "auto";
      } else {
        baseStyle.borderRadius = "20px 20px 20px 6px";
        baseStyle.background = "white";
        baseStyle.border = "1px solid #f1f5f9";
      }

      return baseStyle;
    };

    return (
      <div key={message.id} className={getBubbleClasses()}>
        <div className={getContentClasses()} style={getMessageStyle()}>
          {message.isAudio ? (
            <div className="d-flex align-items-center gap-2">
              <IconMicrophone size={16} />
              <span>Mensagem de áudio</span>
              {message.audioUrl && (
                <audio
                  controls
                  style={{ maxWidth: "200px", height: "30px" }}
                  className="mt-2"
                >
                  <source src={message.audioUrl} type={audioMimeType} />
                  Seu navegador não suporta áudio.
                </audio>
              )}
            </div>
          ) : (
            renderMessageContent(
              message.text,
              message.messageType,
              message.fileName
            )
          )}

          <div
            className={`mt-2 small ${
              message.isUser ? "text-end" : "text-start"
            }`}
          >
            <span
              className={
                message.isUser || message.isError || message.isWarning
                  ? ""
                  : "text-muted"
              }
            >
              {message.timestamp}
              {message.isPartOf && (
                <span className="ms-2">
                  <Badge bg="secondary" className="ms-1">
                    {message.partIndex}/{message.totalParts}
                    {message.messageType === "image" && " 🖼️"}
                  </Badge>
                </span>
              )}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="public-chat-container d-flex flex-column vw-100"
      style={{
        height: "100vh",
        background: "#f8fafc",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        className="public-chat-header bg-white"
        style={{
          minHeight: "65px",
          borderBottom: "1px solid #e2e8f0",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <div className="d-flex align-items-center justify-content-between p-3 h-100">
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: "44px",
                height: "44px",
                background: "var(--gradient-primary)",
                color: "white",
                border: "2px solid #e2e8f0",
              }}
            >
              <IconRobot size={22} />
            </div>
            <div>
              <h6 className="mb-0 fw-semibold" style={{ fontSize: "16px" }}>
                {agent.agent_name}
              </h6>
              <small className="text-muted">
                {agent.agent_job || "Assistente IA"}
              </small>
            </div>
          </div>

          <div className="d-flex gap-2">
            <Button
              variant="outline-warning"
              size="sm"
              onClick={clearConversation}
              title="Limpar conversa"
              className="d-flex align-items-center justify-content-center"
              style={{
                width: "38px",
                height: "38px",
                padding: 0,
                border: "2px solid",
                borderRadius: "12px",
              }}
            >
              <IconTrash size={16} />
            </Button>

            <Button
              variant="outline-danger"
              size="sm"
              onClick={onBack}
              title="Sair"
              className="d-flex align-items-center justify-content-center"
              style={{
                width: "38px",
                height: "38px",
                padding: 0,
                borderRadius: "12px",
              }}
            >
              ✕
            </Button>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div
        className="flex-grow-1 overflow-auto"
        style={{
          padding: "16px",
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
          background: "#f8fafc",
        }}
      >
        {messages.map(renderMessage)}

        {isLoading && (
          <div
            className="d-flex align-items-start mb-3"
            style={{ maxWidth: "85%" }}
          >
            <div
              className="d-flex flex-column gap-2 p-3 bg-white border rounded-3"
              style={{
                borderRadius: "20px 20px 20px 6px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                border: "1px solid #f1f5f9",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-1">
                <small
                  className="text-muted fw-medium"
                  style={{ fontSize: "12px" }}
                >
                  {agent.agent_name}
                </small>
              </div>
              <div className="d-flex gap-1">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="rounded-circle"
                    style={{
                      width: "8px",
                      height: "8px",
                      background: "var(--primary)",
                      animation: `typing 1.4s infinite ${i * 0.2}s`,
                    }}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div
        className="bg-white border-top"
        style={{
          padding: "10px",
          borderTop: "1px solid #f1f5f9",
          background: "white",
          boxShadow: "0 -2px 12px rgba(0,0,0,0.04)",
        }}
      >
        {recordedAudio && (
          <div className="mb-3">
            <div
              className="d-flex align-items-center gap-3 p-3 rounded-3"
              style={{
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: "16px",
              }}
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: "32px",
                  height: "32px",
                  background: "var(--primary)",
                  color: "white",
                }}
              >
                <IconMicrophone size={16} />
              </div>
              <div className="flex-grow-1">
                <small className="text-primary fw-semibold d-block mb-1">
                  Áudio gravado
                </small>
                <audio
                  controls
                  className="w-100"
                  style={{ height: "32px", borderRadius: "8px" }}
                >
                  <source src={recordedAudio} type={audioMimeType} />
                </audio>
              </div>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={sendAudioMessage}
                  disabled={isLoading}
                  style={{
                    borderRadius: "10px",
                    padding: "6px 12px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  Enviar
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={clearRecordedAudio}
                  style={{
                    borderRadius: "10px",
                    padding: "6px 8px",
                    fontSize: "12px",
                  }}
                >
                  ✕
                </Button>
              </div>
            </div>
          </div>
        )}

        <div
          className="d-flex gap-3 align-items-end"
          style={{
            background: "#f8fafc",
            borderRadius: "24px",
            padding: "8px",
            border: "1px solid #e2e8f0",
          }}
        >
          <Form.Control
            as="textarea"
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={`Conversar com ${agent?.agent_name}...`}
            disabled={isLoading}
            rows={1}
            className="flex-grow-1"
            style={{
              resize: "none",
              borderRadius: "18px",
              border: "none",
              background: "transparent",
              padding: "12px 16px",
              minHeight: "44px",
              maxHeight: "120px",
              fontSize: "15px",
              lineHeight: "1.5",
              boxShadow: "none",
              outline: "none",
            }}
          />

          <Button
            variant={isRecording ? "danger" : "outline-primary"}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isLoading}
            title={isRecording ? "Parar gravação" : "Gravar áudio"}
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: "44px",
              height: "44px",
              padding: 0,
              border: "none",
              background: isRecording
                ? "var(--gradient-danger)"
                : recordedAudio
                ? "#f59e0b"
                : "var(--gray-200)",
              color:
                isRecording || recordedAudio ? "white" : "var(--text-muted)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {isRecording ? (
              <IconPlayerStop size={20} />
            ) : recordedAudio ? (
              <IconMicrophoneOff size={20} />
            ) : (
              <IconMicrophone size={20} />
            )}
          </Button>

          <Button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            title="Enviar mensagem"
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: "44px",
              height: "44px",
              padding: 0,
              background:
                inputMessage.trim() && !isLoading
                  ? "var(--gradient-primary)"
                  : "var(--gray-300)",
              border: "none",
              color: "white",
              boxShadow:
                inputMessage.trim() && !isLoading
                  ? "0 2px 12px rgba(59, 130, 246, 0.3)"
                  : "none",
              transform:
                inputMessage.trim() && !isLoading ? "scale(1.05)" : "scale(1)",
              transition: "all 0.2s ease",
            }}
          >
            <IconSend size={20} />
          </Button>
        </div>
      </div>

      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          bg={
            toastType === "error"
              ? "danger"
              : toastType === "warning"
              ? "warning"
              : "primary"
          }
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default PublicChat;
